import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Container,
  Divider,
  Link,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import LandscapeIcon from '@mui/icons-material/Landscape';
import { useWorkspace } from '../context/WorkspaceContext';
import { getTagDetail } from '../utils/shapeUpApi';
import { initialsFor, MEMBER_ROLE_LABELS } from '../components/shapeup/memberRoles';

const OUTCOME_LABEL = {
  shipped: 'Shipped',
  partial: 'Partial',
  dropped: 'Dropped',
};

const OUTCOME_COLOR = {
  shipped: 'success',
  partial: 'warning',
  dropped: 'default',
};

/**
 * Everything tagged one way, and everyone who worked on it.
 *
 * The people half is the point. Tags are recorded on bets because a bet is a
 * finished fact, and this page turns that back into the question people actually
 * ask — "who here has done billing work" — without anyone having maintained a
 * skills list.
 */
const TagPage = () => {
  const { slug } = useParams();
  const { currentOrganization } = useWorkspace();
  const organizationId = currentOrganization?.id;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    try {
      setData(await getTagDetail(slug, organizationId));
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load this tag.');
    } finally {
      setLoading(false);
    }
  }, [slug, organizationId]);

  useEffect(() => { load(); }, [load]);

  const label = data?.tag?.label || slug;
  const bets = data?.bets || [];
  const people = data?.people || [];

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link
          component={RouterLink}
          to="/shape-up"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          color="inherit"
          underline="hover"
        >
          <LandscapeIcon fontSize="small" />
          Shape Up
        </Link>
        <Typography color="text.primary">{label}</Typography>
      </Breadcrumbs>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={<Button color="inherit" size="small" component={RouterLink} to="/shape-up">Back</Button>}
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <Skeleton variant="rectangular" height={320} sx={{ borderRadius: 2 }} />
      ) : (
        <>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>{label}</Typography>
            <Typography variant="body2" color="text.secondary">
              {bets.length === 0
                ? 'Nothing carries this tag yet.'
                : `${bets.length} ${bets.length === 1 ? 'bet' : 'bets'} · ${people.length} ${people.length === 1 ? 'person' : 'people'} who worked on them`}
            </Typography>
          </Box>

          {bets.length === 0 ? (
            <Paper elevation={2} sx={{ p: 5, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>Nothing tagged {label}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Tags are added when you create or edit a bet. Once a few bets carry
                this one, this page shows who has worked in the area.
              </Typography>
              <Button variant="contained" component={RouterLink} to="/shape-up">
                Back to Shape Up
              </Button>
            </Paper>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Paper elevation={2} sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Who has worked on this
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Most bets in this area first. Derived from the bets below, so it
                  stays current on its own.
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  {people.map(person => (
                    <Paper
                      key={person.userId}
                      variant="outlined"
                      sx={{ p: 1.5, display: 'flex', gap: 1.5, alignItems: 'center', minWidth: 240 }}
                    >
                      <Avatar sx={{ width: 34, height: 34, fontSize: 14 }}>
                        {initialsFor(person.name)}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                          {person.name || person.email || 'Unknown'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {person.betCount} {person.betCount === 1 ? 'bet' : 'bets'}
                          {person.role && person.role !== 'member'
                            ? ` · ${MEMBER_ROLE_LABELS[person.role] || person.role}`
                            : ''}
                        </Typography>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              </Paper>

              <Paper elevation={2}>
                <Box sx={{ p: 2, pb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Bets tagged {label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Newest cycle first.
                  </Typography>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Bet</TableCell>
                        <TableCell>Cycle</TableCell>
                        <TableCell>Outcome</TableCell>
                        <TableCell>Team</TableCell>
                        <TableCell>Also tagged</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bets.map(bet => (
                        <TableRow key={bet.id} hover>
                          <TableCell>
                            <Link component={RouterLink} to={`/shape-up/bets/${bet.id}`}>
                              {bet.name}
                            </Link>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {bet.cycleName}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {bet.outcome ? (
                              <Chip
                                size="small"
                                label={OUTCOME_LABEL[bet.outcome] || bet.outcome}
                                color={OUTCOME_COLOR[bet.outcome] || 'default'}
                                variant={bet.outcome === 'shipped' ? 'filled' : 'outlined'}
                                sx={{ height: 20, fontSize: 11 }}
                              />
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                In flight
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {bet.members?.length
                                ? bet.members.map(member => member.name).filter(Boolean).join(', ')
                                : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {bet.tags
                                .filter(tag => tag.slug !== data.tag.slug)
                                .map(tag => (
                                  <Chip
                                    key={tag.slug}
                                    label={tag.label}
                                    size="small"
                                    variant="outlined"
                                    component={RouterLink}
                                    to={`/shape-up/tags/${encodeURIComponent(tag.slug)}`}
                                    clickable
                                    sx={{ height: 20, fontSize: 11 }}
                                  />
                                ))}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default TagPage;
